import moment from "moment-timezone";
import { EFBStaleUnit, IEFBSummaryUser } from "@vmosolutions/oc-interfaces";

/**
 * Shared logic behind the Crew Devices and User Devices admin grids.
 *
 * Both tabs list the same `EFBDeviceSchema` subdocument (crew devices live at
 * `crewInfo.efbDevices`, non-crew user devices at `iOSDevices`), so everything
 * here is deliberately endpoint-agnostic and takes plain `IEFBSummaryUser` rows.
 */

/** Row id prefixes keep crew group rows from colliding with device rows. */
const CREW_ROW_PREFIX = "crew:";
const DEVICE_ROW_PREFIX = "device:";

export const crewRowId = (userId: string): string => `${CREW_ROW_PREFIX}${userId}`;
export const deviceRowId = (deviceId: string): string => `${DEVICE_ROW_PREFIX}${deviceId}`;
export const isCrewRowId = (id: string): boolean => id.startsWith(CREW_ROW_PREFIX);
export const isDeviceRowId = (id: string): boolean => id.startsWith(DEVICE_ROW_PREFIX);
export const deviceIdFromRowId = (id: string): string => id.slice(DEVICE_ROW_PREFIX.length);

/** A device's last-seen instant, and whether it is a real reading or inferred. */
export interface IResolvedLastActive {
  /** Epoch ms, or null when neither a stamp nor a creation date is available. */
  at: number | null;
  /**
   * True when `lastActive` was absent and we fell back to the registration date.
   * Surfaced in the UI so an admin is not shown an invented "last seen" reading.
   */
  inferred: boolean;
}

/**
 * Resolves the timestamp the Last Active column sorts and filters on.
 *
 * Mirrors the server-side filter exactly (`$ifNull: [lastActive, createdAt]`):
 * devices registered before `lastActive` shipped have no stamp, and treating
 * that as "never active" would rank the entire back-catalogue as maximally
 * stale. Falling back to the registration date keeps an old never-used device
 * correctly stale and a just-registered one correctly recent.
 */
export const resolveLastActive = (row: IEFBSummaryUser): IResolvedLastActive => {
  const stamped = row.device?.lastActive;
  if (stamped) {
    const ms = moment(stamped).valueOf();
    if (!Number.isNaN(ms)) return { at: ms, inferred: false };
  }
  const created = row.device?.createdAt;
  if (created) {
    const ms = moment(created).valueOf();
    if (!Number.isNaN(ms)) return { at: ms, inferred: true };
  }
  return { at: null, inferred: false };
};

/** Sort key for the Last Active column. Unknown sorts oldest-first. */
export const lastActiveSortValue = (row: IEFBSummaryUser): number => resolveLastActive(row).at ?? 0;

/** "2y 2mo ago" style relative label, with an absolute date for the tooltip. */
export interface ILastActiveLabel {
  relative: string;
  absolute: string;
  inferred: boolean;
}

export const formatLastActive = (row: IEFBSummaryUser): ILastActiveLabel => {
  const { at, inferred } = resolveLastActive(row);
  if (at === null) return { relative: "Unknown", absolute: "No activity or registration date recorded", inferred };
  const m = moment(at);
  return {
    relative: m.fromNow(),
    absolute: inferred ? `Registered ${m.format("MMM D, YYYY")}; never reported in` : m.format("MMM D, YYYY"),
    inferred,
  };
};

/** Threshold the "not active in more than X" control produces. */
export interface IStaleThreshold {
  amount: number;
  unit: EFBStaleUnit;
}

/** Query string for the server-side staleness filter, or "" when unfiltered. */
export const staleQueryString = (threshold: IStaleThreshold | null): string =>
  threshold ? `?inactiveAmount=${threshold.amount}&inactiveUnit=${threshold.unit}` : "";

/** Human phrase for the results banner, e.g. "the last 3 months". */
export const describeThreshold = (threshold: IStaleThreshold): string => {
  const noun = threshold.amount === 1 ? threshold.unit.replace(/s$/, "") : threshold.unit;
  return `the last ${threshold.amount} ${noun}`;
};

/** One crew member's devices, with the counts the grouped view badges. */
export interface ICrewDeviceGroup {
  userId: string;
  firstName: string;
  lastName: string;
  employeeId: string;
  email: string;
  devices: IEFBSummaryUser[];
  total: number;
  inactive: number;
}

/** Groups flat device rows by owning crew member, preserving encounter order. */
export const groupDevicesByCrew = (rows: IEFBSummaryUser[]): ICrewDeviceGroup[] => {
  const groups = new Map<string, ICrewDeviceGroup>();
  for (const row of rows) {
    const key = row.userId;
    let group = groups.get(key);
    if (!group) {
      group = {
        userId: key,
        firstName: row.firstName,
        lastName: row.lastName,
        employeeId: row.employeeId,
        email: row.email,
        devices: [],
        total: 0,
        inactive: 0,
      };
      groups.set(key, group);
    }
    group.devices.push(row);
    group.total += 1;
    if (!row.device?.active) group.inactive += 1;
  }
  return [...groups.values()];
};

/**
 * A grid row in grouped mode: either a crew group header or a device beneath it.
 *
 * Group rows are supplied explicitly rather than letting the grid auto-generate
 * them, so the header row can carry its own counts and per-crew action.
 */
export type DeviceGridRow =
  | ({ rowType: "device"; id: string; treePath: string[] } & IEFBSummaryUser)
  | { rowType: "crew"; id: string; treePath: string[]; group: ICrewDeviceGroup };

export const isCrewGroupRow = (row: DeviceGridRow): row is Extract<DeviceGridRow, { rowType: "crew" }> =>
  row.rowType === "crew";

/** Flat mode rows: one per device, no tree paths in play. */
export const buildFlatRows = (rows: IEFBSummaryUser[]): DeviceGridRow[] =>
  rows.map((row) => ({
    ...row,
    rowType: "device" as const,
    id: deviceRowId(row.device.deviceId),
    treePath: [row.device.deviceId],
  }));

/** Grouped mode rows: a crew header followed by that crew member's devices. */
export const buildGroupedRows = (groups: ICrewDeviceGroup[]): DeviceGridRow[] => {
  const out: DeviceGridRow[] = [];
  for (const group of groups) {
    out.push({
      rowType: "crew",
      id: crewRowId(group.userId),
      treePath: [group.userId],
      group,
    });
    for (const device of group.devices) {
      out.push({
        ...device,
        rowType: "device",
        id: deviceRowId(device.device.deviceId),
        treePath: [group.userId, device.device.deviceId],
      });
    }
  }
  return out;
};

/**
 * Splits a selection into request-sized batches.
 *
 * The bulk endpoints cap one call at `EFB_BULK_DEVICE_LIMIT`; a "select all
 * matching" over a large fleet can exceed that, so the client chunks rather than
 * failing the whole action.
 */
export const chunk = <T>(items: T[], size: number): T[][] => {
  if (size <= 0) return [items];
  const out: T[][] = [];
  for (let i = 0; i < items.length; i += size) out.push(items.slice(i, i + size));
  return out;
};

/** "1 device" / "3 devices" */
export const pluralise = (count: number, singular: string, plural = `${singular}s`): string =>
  `${count} ${count === 1 ? singular : plural}`;

/** Summary line for the confirmation modal: how many devices, across how many people. */
export const describeSelection = (rows: IEFBSummaryUser[], memberNoun: string): string => {
  const crewCount = new Set(rows.map((r) => r.userId)).size;
  return `${pluralise(rows.length, "device")} belonging to ${pluralise(crewCount, memberNoun)}`;
};

/** "Ashley Callaghan - 50814 - iPad (iPadOS 18.0) - last active May 24, 2024" */
export const describeDeviceForConfirm = (row: IEFBSummaryUser): string => {
  const name = `${row.firstName} ${row.lastName}`.trim();
  const model = `${row.device?.model ?? "Device"}${
    row.state?.systemVersion ? ` (${row.device?.systemName ?? ""} ${row.state.systemVersion})`.replace("  ", " ") : ""
  }`;
  const { relative, inferred } = formatLastActive(row);
  const seen = inferred ? `registered ${relative}` : `last active ${relative}`;
  return [name, row.employeeId, model, seen].filter(Boolean).join(" - ");
};

/**
 * Applies a grid selection change, propagating crew rows to their devices.
 *
 * Extracted and kept pure because this decides what a destructive bulk action
 * operates on, and because the installed DataGrid Pro has no
 * `rowSelectionPropagation` - so this hand-rolled version stands in for a
 * missing library feature and needs to be pinned by tests.
 *
 * Ticking a crew row adds all of its devices; unticking removes only its own,
 * leaving another crew member's devices untouched.
 */
export const applyCrewSelection = (
  before: string[],
  next: string[],
  rowsById: Map<string, DeviceGridRow>
): string[] => {
  const beforeSet = new Set(before);
  const afterSet = new Set(next);
  const result = new Set(afterSet);

  for (const id of afterSet) {
    if (beforeSet.has(id)) continue;
    const row = rowsById.get(id);
    if (row && isCrewGroupRow(row)) {
      row.group.devices.forEach((d) => result.add(deviceRowId(d.device.deviceId)));
    }
  }
  for (const id of beforeSet) {
    if (afterSet.has(id)) continue;
    const row = rowsById.get(id);
    if (row && isCrewGroupRow(row)) {
      row.group.devices.forEach((d) => result.delete(deviceRowId(d.device.deviceId)));
    }
  }

  return [...result];
};

/** What one bulk call returned, as far as the client can tell. */
export interface IBulkChunkOutcome {
  succeeded?: string[];
  failed?: { deviceId: string; reason: string }[];
  usersAffected?: number;
  /** Present when the API wrapper handed back an `{ error }` envelope. */
  error?: string;
}

export interface IBulkFold {
  succeeded: string[];
  failed: { deviceId: string; reason: string }[];
  usersAffected: number;
}

/**
 * Folds one chunk's response into the running total for a bulk action.
 *
 * This decides what the admin is told after a destructive action, so the two
 * "we cannot tell what happened" cases are explicit: the API wrapper resolves
 * to `null` when the request threw, and can hand back an `{ error }` envelope
 * instead of a result. Neither carries a `succeeded` array, so both mark the
 * whole chunk failed rather than silently counting as zero successes.
 */
export const foldBulkChunk = (total: IBulkFold, chunkIds: string[], response: IBulkChunkOutcome | null): IBulkFold => {
  if (!response || !Array.isArray(response.succeeded)) {
    const reason = response?.error ?? "The request did not complete.";
    return {
      succeeded: total.succeeded,
      failed: [...total.failed, ...chunkIds.map((deviceId) => ({ deviceId, reason }))],
      usersAffected: total.usersAffected,
    };
  }
  return {
    succeeded: [...total.succeeded, ...response.succeeded],
    failed: [...total.failed, ...(Array.isArray(response.failed) ? response.failed : [])],
    usersAffected: total.usersAffected + (response.usersAffected ?? 0),
  };
};
